import React from "react";
import { UseFormReturn } from "react-hook-form/dist/types/form";
import { useTranslation } from "react-i18next";
import { formatDateTimeToString } from "go-core/components/Formatters/FormatDate";
import { FormDatePicker } from "go-form/components/FormDatePicker";
import {
	DeliveryEstimationApi,
	LiveOrderAcceptanceFormApi,
	LiveOrderApi,
} from "../../../../../../../../../../../../services/Api/Organization/types";
import {
	MAX_WOLT_DRIVE_DELIVERY_MINUTES,
	isExternalOrderDelivery,
	isWoltDriveAssigned,
} from "../../../../../../services/orderDelivery";
import {
	LiveOrderDeliveryOptionType,
	defaultDeliveryTimeOptions,
	woltDriveTimeOptions,
} from "../../../../../../services/types";
import { getNumberOfMinutesFromNowToTheDate } from "../../../../../../services/utils";
import LiveOrderDeliveryTimeOption from "../LiveOrderDeliveryTimeOptions/LiveOrderDeliveryTimeOption";

interface Props {
	setSelectedTimeOption: (option: string | number | undefined) => void;
	selectedTimeOption: number | string | undefined;
	form: UseFormReturn<LiveOrderAcceptanceFormApi>;
	selectedDeliveryOption: LiveOrderDeliveryOptionType | undefined;
	order: LiveOrderApi;
	orderEstimatedPreparationInMinutes?: number;
	orderDate: string | undefined;
	firstEstimatedDeliveryData: DeliveryEstimationApi | undefined;
	woltDriveScheduleForm: UseFormReturn<{ scheduled_delivery_at?: string }>;
	estimatingLoading: boolean;
}

const LiveOrderAcceptanceModalTimeOptions = ({
	selectedDeliveryOption,
	order,
	firstEstimatedDeliveryData,
	orderEstimatedPreparationInMinutes,
	form,
	selectedTimeOption,
	setSelectedTimeOption,
	woltDriveScheduleForm,
	orderDate,
	estimatingLoading,
}: Props) => {
	const { t } = useTranslation();
	const isWoltDriveSelected = selectedDeliveryOption === LiveOrderDeliveryOptionType.WOLT_DRIVE;
	const isWoltDrive = isWoltDriveAssigned(order);
	const canChooseDeliveryTimeOption = isExternalOrderDelivery(order) || selectedDeliveryOption;
	const firstEstimatedDeliveryTime = firstEstimatedDeliveryData
		? firstEstimatedDeliveryData.delivery_pickup_in_minutes
		: undefined;
	const skipFirstDeliveryEstimation =
		firstEstimatedDeliveryTime !== undefined && firstEstimatedDeliveryTime > MAX_WOLT_DRIVE_DELIVERY_MINUTES;

	const getUniqueWoltDriveOptions = () => {
		let options: number[] = [...woltDriveTimeOptions];

		if (skipFirstDeliveryEstimation !== true) {
			if (firstEstimatedDeliveryTime) {
				options = [...new Set([...options, firstEstimatedDeliveryTime])];
			}

			if (
				isWoltDrive &&
				orderEstimatedPreparationInMinutes !== undefined &&
				orderEstimatedPreparationInMinutes >= 0
			) {
				options = [...new Set([...options, orderEstimatedPreparationInMinutes])];
			}
		}

		return options.sort((a, b) => a - b).filter((option) => option !== 0);
	};
	const getOptions = () => {
		return isWoltDriveSelected ? getUniqueWoltDriveOptions() : defaultDeliveryTimeOptions;
	};

	const handleSelectDeliveryTimeOption = () => {
		woltDriveScheduleForm.setValue("scheduled_delivery_at", "");
		form.setValue("pickup_at", "");
	};

	return canChooseDeliveryTimeOption ? (
		<div className="position-relative live-order-delivery-time-options">
			{estimatingLoading && <div className="overlay" />}
			<h5>
				{!isWoltDriveSelected
					? t("modules.live_order.field.choose_delivery_time.title")
					: t("modules.live_order.field.choose_preparation_time_for_driver.title")}
			</h5>
			<div
				className={`d-flex flex-wrap gap-2 ${
					isWoltDriveSelected ? "align-items-start" : "align-items-center"
				} live-order-delivery-time-options-container ${isWoltDriveSelected ? "without-limited-height" : ""} ${
					isWoltDriveSelected ? "" : " with-full-width"
				}`}
			>
				{getOptions().map((option) => (
					<LiveOrderDeliveryTimeOption
						setSelectedTimeOption={setSelectedTimeOption}
						option={option}
						selectedTimeOption={selectedTimeOption}
						key={option.toString()}
						handleSelect={handleSelectDeliveryTimeOption}
					/>
				))}
				{isWoltDriveSelected ? (
					<FormDatePicker
						control={woltDriveScheduleForm.control}
						name="scheduled_delivery_at"
						errors={woltDriveScheduleForm.formState.errors}
						allowResetDate
						onApply={(date) => {
							setSelectedTimeOption(date ? formatDateTimeToString(date as Date) : undefined);
						}}
						help={t("modules.live_order.field.execution_at.help_text")}
						placeholder={t("modules.live_order.field.execution_at.placeholder")}
						onClear={() => {
							setSelectedTimeOption(undefined);
							woltDriveScheduleForm?.setValue("scheduled_delivery_at", "");
						}}
						onToggle={(isOpen) => {
							if (isOpen && orderDate && getNumberOfMinutesFromNowToTheDate(orderDate) !== 0) {
								setSelectedTimeOption(orderDate);
								woltDriveScheduleForm?.setValue("scheduled_delivery_at", orderDate);
							}
						}}
					/>
				) : (
					<FormDatePicker
						control={form.control}
						name="pickup_at"
						errors={form.formState.errors}
						allowResetDate
						onApply={(date) => {
							setSelectedTimeOption(date as string | undefined);
						}}
						onClear={() => {
							setSelectedTimeOption(undefined);
							form?.setValue("pickup_at", "");
						}}
						placeholder={t("modules.live_order.field.execution_at.placeholder")}
						onToggle={(isOpen) => {
							if (isOpen && orderDate && getNumberOfMinutesFromNowToTheDate(orderDate) !== 0) {
								setSelectedTimeOption(orderDate);
								form.setValue("pickup_at", orderDate);
							}
						}}
					/>
				)}
			</div>
		</div>
	) : (
		<></>
	);
};

export default LiveOrderAcceptanceModalTimeOptions;
